import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, ChevronRight, Shield, Award, Zap, Clock, AlertCircle, X } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { PLANNING_DATA } from '../data/products';
import { REALISATIONS } from '../data/realisations';

const ScrollReveal = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) el.classList.add('revealed'); },
      { threshold: 0.08 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} className={`scroll-reveal ${delay ? `delay-${delay}` : ''} ${className}`}>
      {children}
    </div>
  );
};

// Lightbox Component
const Lightbox = ({ image, name, onClose }) => {
  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'auto';
    };
  }, [onClose]);

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
      onClick={onClose}
    >
      <button 
        className="absolute top-4 right-4 text-white hover:text-[#F5820D] transition-colors"
        onClick={onClose}
      >
        <X size={32} />
      </button>
      <div 
        className="max-w-4xl max-h-[90vh] flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img 
          src={image} 
          alt={name} 
          className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
        />
        <p className="font-oswald text-white text-xl uppercase mt-4">{name}</p>
      </div>
    </div>
  );
};

const PACK_CONFIGS = [
  {
    key: 'essential',
    number: '01',
    accent: '#CD853F',
    bg: '#161616',
    banner: null,
  },
  {
    key: 'performance',
    number: '02',
    accent: '#F5820D',
    bg: '#0C1E2D',
    banner: 'RECOMMANDÉ',
    popular: true,
  },
  {
    key: 'premium',
    number: '03',
    accent: '#F5C842',
    bg: '#111111',
    banner: null,
  },
];

export default function Products() {
  const { t } = useTranslation();
  const [lightboxImage, setLightboxImage] = useState(null);

  const planningKeys = ['tshirt', 'cup', 'buff', 'cap', 'socks', 'medal', 'backpack', 'packaging'];

  return (
    <div className="min-h-screen bg-white">
      {/* Lightbox */}
      {lightboxImage && (
        <Lightbox 
          image={lightboxImage.src} 
          name={lightboxImage.name} 
          onClose={() => setLightboxImage(null)} 
        />
      )}

      {/* HERO BANNER */}
      <section
        className="relative pt-24 pb-10 md:pt-32 md:pb-16 flex items-center"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/2402777/pexels-photo-2402777.jpeg?auto=compress&cs=tinysrgb&w=1200)',
          backgroundSize: 'cover',
          backgroundPosition: 'center 40%',
        }}
        data-testid="products-hero"
      >
        <div className="absolute inset-0 bg-[#1B4F6B]/90" />
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-1 h-12 bg-[#F5820D]" />
            <div>
              <h1 className="font-oswald font-bold text-white uppercase leading-none" style={{ fontSize: 'clamp(2rem, 6vw, 4rem)' }}>
                {t('products.title')}
              </h1>
              <p className="font-manrope text-white/70 text-base mt-2">{t('products.subtitle')}</p>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 40" preserveAspectRatio="none" className="w-full h-10">
            <path d="M0 40 L0 20 L360 5 L720 25 L1080 5 L1440 20 L1440 40 Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* SECTION A — PACKS */}
      <section
        className="relative overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/2402777/pexels-photo-2402777.jpeg?auto=compress&cs=tinysrgb&w=1600)',
          backgroundSize: 'cover',
          backgroundPosition: 'center 40%',
          backgroundAttachment: 'fixed',
        }}
        data-testid="section-packs"
      >
        <div className="absolute inset-0" style={{ backgroundColor: 'rgba(8,12,16,0.88)' }} />
        
        {/* PROMO BANNER - Inside packs section */}
        <div className="relative z-10 border-b border-[#F5820D]/30">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 md:py-5">
            <div className="flex flex-col md:flex-row items-center justify-center gap-3 md:gap-6 text-center">
              <div className="flex flex-wrap items-center justify-center gap-2 md:gap-4">
                <span className="bg-[#F5820D] text-[#111] font-oswald text-xs md:text-sm font-bold uppercase px-3 py-1.5 rounded-sm tracking-wider">
                  {t('home.promo.badge')}
                </span>
                <span className="font-manrope text-white text-sm md:text-base">
                  {t('home.promo.text')}
                </span>
                <span className="font-oswald text-[#F5820D] text-xl md:text-2xl font-bold">
                  {t('home.promo.price')}
                </span>
              </div>
              <Link 
                to="/contact"
                className="bg-[#F5820D] hover:bg-[#D9730B] text-[#111] font-oswald text-xs md:text-sm font-bold uppercase px-5 md:px-6 py-2 rounded-sm transition-all duration-300 tracking-wider"
              >
                {t('home.promo.cta')} →
              </Link>
            </div>
          </div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-20">
          <ScrollReveal>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-1 h-10 bg-[#C0392B]" />
              <h2 className="font-oswald text-3xl md:text-4xl font-bold uppercase text-white">
                {t('products.packs_title')}
              </h2>
            </div>
            <p className="font-manrope text-white/40 text-base mb-6 md:mb-12 ml-5">{t('products.packs_subtitle')}</p>
          </ScrollReveal>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 lg:gap-px" style={{ backgroundColor: '#2a2a2a' }}>
            {PACK_CONFIGS.map((cfg, idx) => {
              const pack = t(`products.packs.${cfg.key}`, { returnObjects: true });
              const services = t(`products.packs.${cfg.key}.services`, { returnObjects: true });
              const packProducts = t(`products.packs.${cfg.key}.products`, { returnObjects: true });
              return (
                <ScrollReveal key={cfg.key} delay={idx * 100}>
                  <div
                    className="relative overflow-hidden flex flex-col h-full hover:-translate-y-0.5 transition-transform duration-300"
                    style={{ backgroundColor: cfg.bg }}
                    data-testid={`products-pack-${cfg.key}`}
                  >
                    {/* Watermark number */}
                    <div
                      className="absolute -bottom-6 -right-2 font-oswald font-bold leading-none select-none pointer-events-none"
                      style={{ fontSize: '200px', color: cfg.accent, opacity: 0.04 }}
                    >
                      {cfg.number}
                    </div>

                    {/* Top accent bar */}
                    <div style={{ height: '3px', backgroundColor: cfg.accent }} />

                    {/* Banner */}
                    {cfg.banner && (
                      <div
                        className="text-[#111] text-base md:text-lg font-oswald font-bold uppercase tracking-widest text-center py-3 md:py-4"
                        style={{ backgroundColor: cfg.accent }}
                      >
                        ★ {cfg.banner} ★
                      </div>
                    )}

                    <div className="relative z-10 p-4 md:p-7 flex flex-col flex-1">
                      {/* Header */}
                      <div className="mb-3 md:mb-5">
                        <div className="flex items-center justify-between mb-2 md:mb-3">
                          <span className="font-oswald text-xs tracking-widest font-bold" style={{ color: cfg.accent }}>
                            {cfg.number} — PACK
                          </span>
                          <span
                            className="text-[#111] text-xs font-bold uppercase px-3 py-1"
                            style={{ backgroundColor: cfg.accent }}
                          >
                            {pack.badge}
                          </span>
                        </div>
                        <h3 className="font-oswald text-xl md:text-2xl font-bold uppercase text-white leading-tight mb-1">
                          {pack.name}
                        </h3>
                        <p className="font-manrope text-xs md:text-sm" style={{ color: cfg.accent + 'AA' }}>
                          {pack.tagline}
                        </p>
                      </div>

                      {/* Badges */}
                      <div className="flex flex-wrap gap-1.5 md:gap-2 mb-3 md:mb-5">
                        <span
                          className="font-oswald text-xs uppercase tracking-wider px-2 md:px-3 py-1 md:py-1.5 border"
                          style={{ color: cfg.accent, borderColor: cfg.accent + '40' }}
                        >
                          {pack.participants}
                        </span>
                        {pack.loyalty && (
                          <span
                            className="font-oswald text-xs font-bold uppercase px-2 md:px-3 py-1 md:py-1.5 text-[#111]"
                            style={{ backgroundColor: cfg.accent }}
                          >
                            {pack.loyalty}
                          </span>
                        )}
                      </div>

                      {/* Divider */}
                      <div style={{ height: '1px', backgroundColor: cfg.accent, opacity: 0.15, marginBottom: '0.75rem' }} />

                      {/* Products included */}
                      <div className="mb-3 md:mb-5 flex-1">
                        <h4
                          className="font-oswald text-xs uppercase tracking-widest mb-2 md:mb-3"
                          style={{ color: cfg.accent }}
                        >
                          {t('products.products_included')}
                        </h4>
                        <ul className="space-y-1 md:space-y-2">
                          {Array.isArray(packProducts) && packProducts.map((item, i) => (
                            <li key={i} className="flex items-start gap-2 font-manrope text-white/60" style={{ fontSize: '11px' }}>
                              <span style={{ color: cfg.accent, flexShrink: 0, marginTop: '3px', fontSize: '7px' }}>▶</span>
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Services */}
                      <div style={{ borderTop: `1px solid ${cfg.accent}20`, paddingTop: '0.6rem', marginBottom: '0.75rem' }}>
                        <h4 className="font-oswald text-xs uppercase tracking-widest mb-1.5 text-white/30">
                          {t('products.services_included')}
                        </h4>
                        <ul className="space-y-1">
                          {Array.isArray(services) && services.map((s, i) => (
                            <li key={i} className="flex items-start gap-2 font-manrope text-white/40" style={{ fontSize: '10px' }}>
                              <Check size={9} className="flex-shrink-0 mt-0.5" style={{ color: cfg.accent + '70' }} />
                              {s}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* CTA */}
                      <button
                        onClick={() => { window.location.href = '/contact'; }}
                        className="w-full font-oswald uppercase tracking-wider text-sm py-4 font-bold transition-opacity duration-200 hover:opacity-85"
                        style={{ backgroundColor: cfg.accent, color: '#111111' }}
                        data-testid={`pack-quote-${cfg.key}`}
                      >
                        {t('products.request_quote')} →
                      </button>
                    </div>
                  </div>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION B — NOS RÉALISATIONS */}
      <section className="py-10 md:py-20 bg-[#F8F9FA]" data-testid="section-catalog">
        <div className="max-w-7xl mx-auto px-6">
          <ScrollReveal>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-1 h-10 bg-[#F5820D]" />
              <h2 className="font-oswald text-3xl md:text-4xl font-bold uppercase text-[#111111]">
                {t('products.catalog_title')}
              </h2>
            </div>
            <p className="font-manrope text-gray-600 text-base mb-6 md:mb-10 ml-5">{t('products.catalog_subtitle')}</p>
          </ScrollReveal>

          {/* Realisations grid - simple photo + name cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
            {REALISATIONS.map((item, idx) => (
              <div 
                key={idx} 
                className="bg-white rounded-lg overflow-hidden border border-gray-200 shadow-md cursor-pointer group hover:shadow-lg transition-shadow duration-300"
                onClick={() => setLightboxImage(item)}
              >
                <div className="relative aspect-square overflow-hidden bg-gray-100">
                  <img
                    src={item.src}
                    alt={item.name}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    onError={(e) => { e.target.src = 'https://images.pexels.com/photos/1571939/pexels-photo-1571939.jpeg?auto=compress&cs=tinysrgb&w=400'; }}
                  />
                </div>
                <div className="p-3 md:p-4">
                  <h3 className="font-oswald text-sm md:text-base font-bold uppercase text-[#111111] leading-tight group-hover:text-[#F5820D] transition-colors duration-300 text-center">
                    {item.name}
                  </h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION C — PLANNING INVERSÉ */}
      <section className="py-10 md:py-20 bg-white" data-testid="section-planning">
        <div className="max-w-7xl mx-auto px-6">
          <ScrollReveal>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-1 h-10 bg-[#1B4F6B]" />
              <h2 className="font-oswald text-3xl md:text-4xl font-bold uppercase text-[#111111]">
                {t('products.planning_title')}
              </h2>
            </div>
            <p className="font-manrope text-gray-600 text-base mb-6 md:mb-10 ml-5">{t('products.planning_subtitle')}</p>
          </ScrollReveal>

          {/* Timeline visual */}
          <ScrollReveal>
            <div className="bg-[#1B4F6B] rounded-sm p-3 md:p-6 mb-4 md:mb-8">
              <div className="flex flex-wrap md:flex-nowrap md:flex-row items-center justify-between gap-2 md:gap-3 text-center">
                {[
                  { label: 'Devis signé', color: 'bg-[#F5820D]', arrow: true },
                  { label: 'BAT validé', color: 'bg-[#F5C842]', arrow: true },
                  { label: 'Lancement', color: 'bg-white/20', arrow: true },
                  { label: 'Livraison J-7', color: 'bg-white/20', arrow: true },
                  { label: 'ÉVÉNEMENT', color: 'bg-[#C0392B]', arrow: false },
                ].map((step, i) => (
                  <div key={i} className="flex items-center gap-1 md:gap-3">
                    <div className={`${step.color} px-2 md:px-3 py-1.5 md:py-2 rounded-sm`}>
                      <span className="font-oswald text-white text-xs uppercase tracking-wider whitespace-nowrap">{step.label}</span>
                    </div>
                    {step.arrow && <ChevronRight size={14} className="text-white/40 hidden md:block" />}
                  </div>
                ))}
              </div>
            </div>
          </ScrollReveal>

          {/* Table */}
          <ScrollReveal delay={100}>
            <div className="overflow-x-auto rounded-sm border border-gray-200 shadow-sm">
              <table className="w-full" data-testid="planning-table">
                <thead>
                  <tr className="bg-[#111111]">
                    <th className="font-oswald text-white text-left px-5 py-4 text-sm uppercase tracking-wider">Produit</th>
                    <th className="font-oswald text-amber-400 text-center px-5 py-4 text-sm uppercase tracking-wider">Essentiel</th>
                    <th className="font-oswald text-gray-300 text-center px-5 py-4 text-sm uppercase tracking-wider">Performance</th>
                    <th className="font-oswald text-[#F5C842] text-center px-5 py-4 text-sm uppercase tracking-wider">Premium</th>
                  </tr>
                </thead>
                <tbody>
                  {PLANNING_DATA.map((row, i) => (
                    <tr key={row.key} className={`border-t border-gray-100 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition-colors`}>
                      <td className="font-manrope text-gray-800 text-sm px-5 py-3 font-medium">
                        {t(`products.planning_products.${row.key}`)}
                      </td>
                      <td className="text-center px-5 py-3">
                        {row.essential ? (
                          <span className="font-manrope text-sm text-amber-700 font-medium">{row.essential}</span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="text-center px-5 py-3">
                        {row.performance ? (
                          <span className="font-manrope text-sm text-slate-600 font-medium">{row.performance}</span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="text-center px-5 py-3">
                        {row.premium ? (
                          <span className="font-manrope text-sm text-yellow-700 font-medium">{row.premium}</span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="font-manrope text-gray-500 text-xs mt-4 flex items-start gap-2">
              <AlertCircle size={13} className="text-[#F5820D] flex-shrink-0 mt-0.5" />
              {t('products.planning_note')}
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* SECTION D — QUALITÉ */}
      <section
        className="py-10 md:py-20 relative"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/1571939/pexels-photo-1571939.jpeg?auto=compress&cs=tinysrgb&w=1200)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="section-quality"
      >
        <div className="absolute inset-0 bg-[#0d0d0d]/65" />
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <ScrollReveal>
            <div className="text-center mb-6 md:mb-12">
              <div className="flex items-center justify-center gap-4 mb-4">
                <div className="w-1 h-10 bg-[#F5820D]" />
                <h2 className="font-oswald text-3xl md:text-4xl font-bold uppercase text-white">
                  {t('products.quality_title')}
                </h2>
                <div className="w-1 h-10 bg-[#F5820D]" />
              </div>
              <div className="inline-block mt-3 border border-[#F5C842]/70 bg-[#F5C842]/20 px-4 md:px-6 py-2 md:py-3 rounded-sm max-w-2xl">
                <p className="font-manrope text-white text-xs md:text-sm italic">
                  "{t('products.quality_badge')}"
                </p>
              </div>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-3 gap-3 md:gap-6 mb-6 md:mb-12">
            {[
              { icon: Award, titleKey: 'medals_title', textKey: 'medals_text', color: 'text-[#F5C842]' },
              { icon: Zap, titleKey: 'textile_title', textKey: 'textile_text', color: 'text-[#F5820D]' },
              { icon: Shield, titleKey: 'accessories_title', textKey: 'accessories_text', color: 'text-[#1B9BB0]' },
            ].map((item, i) => (
              <ScrollReveal key={i} delay={i * 100}>
                <div className="bg-white/5 border border-white/10 rounded-sm p-3 md:p-6 hover:bg-white/10 transition-colors h-full">
                  <item.icon size={22} className={`${item.color} mb-2 md:mb-4`} />
                  <h3 className="font-oswald text-white text-base md:text-lg uppercase tracking-wider mb-2 md:mb-3">
                    {t(`products.quality.${item.titleKey}`)}
                  </h3>
                  <p className="font-manrope text-white/90 text-xs md:text-sm leading-relaxed">
                    {t(`products.quality.${item.textKey}`)}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>

          <ScrollReveal>
            <div className="text-center">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-[#F5820D] hover:bg-[#D9730B] text-white font-oswald uppercase tracking-widest text-sm px-8 py-4 transition-all duration-300 hover:-translate-y-0.5"
                data-testid="quality-cta"
              >
                {t('products.request_quote')} <ChevronRight size={15} />
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

    </div>
  );
}
